import type { Ref } from 'vue'
import { doc, getDocs, query, collection, where } from 'firebase/firestore'
import { db } from '../firebase'
import { setDoc, deleteDoc, updateDoc } from './firestoreTracked'
import type { RecurringPurchase, ShoppingCartItem, Article } from '../data/mockData'
import { QUOTA_CONFIG } from '../constants/quotaConfig'

export interface RecurringPurchasesContext {
  recurringPurchases: Ref<RecurringPurchase[]>
  shoppingList: Ref<ShoppingCartItem[]>
  articles: Ref<Article[]>
  activeListId: Ref<string>
  getUid: () => string
  addShoppingItem: (articleID: string, amount: string, note: string, listId?: string, recurringID?: string) => Promise<string | null>
  updateShoppingItem: (item: ShoppingCartItem) => Promise<void>
  showToast: (msg: string, type?: 'info' | 'success' | 'warning' | 'error') => void
  isShoppingListSyncing?: Ref<boolean>
  isSameList?: (listA?: string, listB?: string) => boolean
}

export function createRecurringPurchasesService(ctx: RecurringPurchasesContext) {
  const {
    recurringPurchases,
    shoppingList,
    articles,
    activeListId,
    getUid,
    addShoppingItem,
    updateShoppingItem,
    showToast,
    isShoppingListSyncing,
    isSameList
  } = ctx

  const addRecurringPurchase = async (articleID: string, amount: string, intervalDays: number, listId?: string) => {
    if (recurringPurchases.value.length >= QUOTA_CONFIG.MAX_RECURRING_PURCHASES_PER_USER) {
      showToast(`Maximale Anzahl an wiederkehrenden Einkäufen erreicht (${QUOTA_CONFIG.MAX_RECURRING_PURCHASES_PER_USER}).`, 'error')
      return null
    }
    const uid = getUid()
    const targetListId = listId || activeListId.value || uid
    const id = 'rec-' + Date.now().toString() + '-' + Math.random().toString(36).substring(2, 9)

    const newRec: RecurringPurchase = {
      id,
      articleID,
      amount,
      intervalDays,
      lastAdded: 0,
      enabled: true,
      listId: targetListId,
      ownerId: uid
    }

    await setDoc(doc(db, 'recurring_purchases', id), newRec)
    if (!recurringPurchases.value.some(p => p.id === id)) {
      recurringPurchases.value.push(newRec)
    }
    await checkAndProcessRecurringPurchases()
    return id
  }

  const updateRecurringPurchase = async (item: RecurringPurchase) => {
    await setDoc(doc(db, 'recurring_purchases', item.id), { ...item })
  }

  const deleteRecurringPurchase = async (id: string) => {
    await deleteDoc(doc(db, 'recurring_purchases', id))
  }

  let isChecking = false

  const checkAndProcessRecurringPurchases = async () => {
    if (isChecking) return
    const uid = getUid()
    if (!uid) return
    if (isShoppingListSyncing?.value) return

    isChecking = true
    try {
      if (articles.value.length === 0) return
      const activePurchases = recurringPurchases.value.filter(p => p.enabled)
      if (activePurchases.length === 0) return

      const now = Date.now()
      const currentActiveListId = activeListId.value

      const isTargetActive = (targetList: string) => {
        if (isSameList) {
          return isSameList(targetList, currentActiveListId)
        }
        return currentActiveListId ? targetList === currentActiveListId : false
      }

      for (const rec of activePurchases) {
        const targetListId = rec.listId || currentActiveListId || ('shop-list-default-' + uid)

        if (isTargetActive(targetListId)) {
          // Target is active list and in-memory shoppingList is authoritative
          const itemsForArticle = shoppingList.value.filter(item => item.articleID === rec.articleID)
          const openItem = itemsForArticle.find(item => !item.checked)
          const checkedItem = itemsForArticle.find(item => item.checked)

          if (openItem) {
            // Already open on the list - ensure recurringID is linked
            if (!openItem.recurringID) {
              await updateShoppingItem({
                ...openItem,
                recurringID: rec.id
              })
            }
            continue
          }

          if (checkedItem) {
            const checkedAtMs = typeof checkedItem.checkedAt === 'number' ? checkedItem.checkedAt : 0
            const lastFulfilledAt = Math.max(rec.lastAdded || 0, checkedAtMs)
            const diffDays = lastFulfilledAt > 0 ? (now - lastFulfilledAt) / (1000 * 60 * 60 * 24) : 0

            // If checked off recently (< intervalDays / e.g. < 1 day ago), DO NOT duplicate or reactivate
            if (lastFulfilledAt > 0 && diffDays < rec.intervalDays) {
              if ((rec.lastAdded || 0) < lastFulfilledAt) {
                rec.lastAdded = lastFulfilledAt
                try {
                  await updateDoc(doc(db, 'recurring_purchases', rec.id), { lastAdded: lastFulfilledAt })
                } catch {}
              }
              continue
            }

            // Interval has truly elapsed since it was checked off: reactivate existing checked item
            await updateShoppingItem({
              ...checkedItem,
              checked: false,
              checkedAt: undefined,
              amount: rec.amount || checkedItem.amount,
              recurringID: rec.id
            })
            rec.lastAdded = now
            try {
              await updateDoc(doc(db, 'recurring_purchases', rec.id), { lastAdded: now })
            } catch {}
            continue
          }

          // No item exists on active list: check interval from lastAdded
          const lastAdded = rec.lastAdded || 0
          const diffDays = lastAdded > 0 ? (now - lastAdded) / (1000 * 60 * 60 * 24) : 0
          if (lastAdded === 0 || diffDays >= rec.intervalDays) {
            await addShoppingItem(rec.articleID, rec.amount, '', targetListId, rec.id)
            rec.lastAdded = now
            try {
              await updateDoc(doc(db, 'recurring_purchases', rec.id), { lastAdded: now })
            } catch {}
          }
        } else {
          // Target is another list
          try {
            const qCheck = query(
              collection(db, 'shoppingList'),
              where('listId', '==', targetListId),
              where('articleID', '==', rec.articleID)
            )
            const snap = await getDocs(qCheck)
            if (!snap.empty) {
              const docs = snap.docs
              const openDoc = docs.find(d => !d.data().checked)
              const checkedDoc = docs.find(d => d.data().checked)

              if (openDoc) {
                if (!openDoc.data().recurringID) {
                  try {
                    await updateDoc(doc(db, 'shoppingList', openDoc.id), { recurringID: rec.id })
                  } catch {}
                }
                continue
              }

              if (checkedDoc) {
                const data = checkedDoc.data()
                const checkedAtMs = typeof data.checkedAt === 'number' ? data.checkedAt : 0
                const lastFulfilledAt = Math.max(rec.lastAdded || 0, checkedAtMs)
                const diffDays = lastFulfilledAt > 0 ? (now - lastFulfilledAt) / (1000 * 60 * 60 * 24) : 0

                if (lastFulfilledAt > 0 && diffDays < rec.intervalDays) {
                  if ((rec.lastAdded || 0) < lastFulfilledAt) {
                    rec.lastAdded = lastFulfilledAt
                    try {
                      await updateDoc(doc(db, 'recurring_purchases', rec.id), { lastAdded: lastFulfilledAt })
                    } catch {}
                  }
                  continue
                }

                // Reactivate checked doc in target list
                try {
                  await updateDoc(doc(db, 'shoppingList', checkedDoc.id), {
                    checked: false,
                    checkedAt: null,
                    amount: rec.amount || data.amount,
                    recurringID: rec.id
                  })
                } catch (err) {
                  console.warn('[RecurringPurchases] Could not reactivate target list doc in Firestore:', err)
                }
                rec.lastAdded = now
                try {
                  await updateDoc(doc(db, 'recurring_purchases', rec.id), { lastAdded: now })
                } catch {}
                continue
              }
            }

            // No item exists in target list
            const lastAdded = rec.lastAdded || 0
            const diffDays = lastAdded > 0 ? (now - lastAdded) / (1000 * 60 * 60 * 24) : 0
            if (lastAdded === 0 || diffDays >= rec.intervalDays) {
              await addShoppingItem(rec.articleID, rec.amount, '', targetListId, rec.id)
              rec.lastAdded = now
              try {
                await updateDoc(doc(db, 'recurring_purchases', rec.id), { lastAdded: now })
              } catch {}
            }
          } catch (err) {
            console.warn('[RecurringPurchases] Could not check target list in Firestore:', err)
          }
        }
      }
    } finally {
      isChecking = false
    }
  }

  return {
    addRecurringPurchase,
    updateRecurringPurchase,
    deleteRecurringPurchase,
    checkAndProcessRecurringPurchases
  }
}
